import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigation } from "@/hooks/useNavigation";

export const SidebarNavItem = observer(
  ({ item, depth = 0, collapsed = false }) => {
    const { label, icon: Icon, link, subItems } = item;
    const { t } = useTran();
    const [isExpanded, setIsExpanded] = useState(false);
    const navigate = useNavigate();
    const { isItemActive } = useNavigation();

    const isActive = isItemActive(item);

    const hasLink = Boolean(link);
    const hasSubItems = Boolean(subItems && subItems.length > 0);

    // Auto-expand when active
    useEffect(() => {
      if (isActive && hasSubItems) {
        setIsExpanded(true);
      }
    }, [isActive, hasSubItems]);

    const handleClick = () => {
      if (hasSubItems) {
        setIsExpanded(!isExpanded);
        return;
      }
      if (hasLink) {
        navigate(link.toLowerCase());
      }
    };

    // Active state colors using theme primary color
    const activeClasses =
      depth === 0
        ? "border-l-4 border-primary bg-primary/10 text-primary ring-1 ring-primary/20"
        : "bg-primary/5 text-primary";

    const hoverClasses = "text-foreground hover:bg-muted";
    const disabledClasses =
      !hasLink && !hasSubItems ? "cursor-not-allowed opacity-50" : "";
    const labelText = label ? t(label) : "";

    // Collapsed icon-only view
    if (collapsed && depth === 0) {
      const iconActiveClass =
        "border-l-4 border-primary bg-primary/10 text-primary ring-1 ring-primary/20 rounded-radius";

      return (
        <button
          type="button"
          onClick={handleClick}
          title={labelText}
          className={cn(
            "group relative flex w-full items-center justify-center rounded-radius px-2 py-2 transition-colors hover:bg-muted text-foreground",
            isActive && iconActiveClass,
          )}
        >
          {Icon && (
            <Icon
              className={cn(
                "h-5 w-5",
                isActive ? "text-primary" : "text-foreground",
              )}
            />
          )}
        </button>
      );
    }

    return (
      <div className={depth > 0 ? "ml-4" : undefined}>
        <button
          type="button"
          onClick={handleClick}
          disabled={!hasLink && !hasSubItems}
          className={cn(
            "group relative flex w-full items-center gap-3 rounded-radius px-3 py-2 transition-colors",
            isActive ? activeClasses : hoverClasses,
            disabledClasses,
          )}
        >
          {Icon && <Icon className="h-4 w-4" />}
          <span className="flex-1 text-left text-sm">{labelText}</span>
          {hasSubItems && (
            <span className="flex items-center justify-center text-muted-foreground">
              {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </span>
          )}
        </button>

        {hasSubItems && isExpanded && (
          <div className="mt-1 space-y-1">
            {subItems.map((child) => (
              <SidebarNavItem
                key={child.label}
                item={child}
                depth={depth + 1}
                collapsed={collapsed}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
);
